import type { ReactElement } from 'react'

import type { LocaleCode } from '@/lib/locales'
import { cn } from '@/lib/utils'

/**
 * Tiny SVG flags for the language picker.
 *
 * Emoji flags are out: Windows ships no flag glyphs at all, so `🇵🇱` renders
 * there as the bare letters `PL` in a box, and every other platform draws its
 * own. Hand-drawn rects render identically everywhere and cost nothing.
 *
 * Each one fills a rounded box via `preserveAspectRatio="xMidYMid slice"`, so
 * flags with different native ratios still read at the same size in the menu.
 */
const FLAGS: Record<LocaleCode, ReactElement> = {
  pl: (
    <svg viewBox="0 0 20 14" preserveAspectRatio="xMidYMid slice" className="h-full w-full">
      <title>Polska</title>
      <rect width="20" height="7" fill="#ffffff" />
      <rect y="7" width="20" height="7" fill="#DC143C" />
    </svg>
  ),
  // Union Jack, drawn with symmetric diagonals — the real flag's counterchange
  // offset is sub-pixel at 20px wide, and skipping it avoids clip-path ids that
  // would collide between instances.
  en: (
    <svg viewBox="0 0 60 30" preserveAspectRatio="xMidYMid slice" className="h-full w-full">
      <title>United Kingdom</title>
      <rect width="60" height="30" fill="#012169" />
      <path d="M0,0 L60,30 M60,0 L0,30" stroke="#ffffff" strokeWidth="8" />
      <path d="M0,0 L60,30 M60,0 L0,30" stroke="#C8102E" strokeWidth="4" />
      <path d="M30,0 V30 M0,15 H60" stroke="#ffffff" strokeWidth="12" />
      <path d="M30,0 V30 M0,15 H60" stroke="#C8102E" strokeWidth="7" />
    </svg>
  ),
}

export function LocaleFlag({ locale, className }: { locale: LocaleCode; className?: string }) {
  return (
    <span
      aria-hidden="true"
      className={cn(
        'inline-block shrink-0 overflow-hidden rounded-[3px] ring-1 ring-white/20',
        className,
      )}
    >
      {FLAGS[locale]}
    </span>
  )
}
